import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  MultiSelect,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ResumeLink } from "../../components/ResumeLink";
import { ProfileKeyFacts } from "../../components/profile/ProfileKeyFacts";
import { ProfileShell } from "../../components/profile/ProfileShell";
import { ProfileTimeline } from "../../components/profile/ProfileTimeline";
import { ConfirmDialog, LoadingOverlay } from "../../components/ui";
import { renderSegmentOption } from "../../components/SegmentOptionLabel";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { adminApi, profilesApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { ACCESS_GRANT_COPY, CONFIRM_DIALOG_COPY } from "../../lib/confirmDialog";
import { formatDate, SEGMENT_LABELS } from "../../lib/format";
import { IDENTITY_KYC_SLOT_LABELS, kycStatusLabel as kycPackLabel } from "../../lib/kyc";
import { SEGMENT_SELECT_COPY } from "../../lib/segments";
import { useSegmentCatalog } from "../../lib/useSegmentCatalog";
import { axioTokens } from "../../theme";
import type { Segment } from "../../types/api";

const SLOT_LABELS = IDENTITY_KYC_SLOT_LABELS;

const RECRUITER_PROFILE_COPY = {
  back: "Recruiters",
  role: "Recruiter",
  reactivateCta: "Reactivate recruiter",
  deactivateCta: "Deactivate recruiter",
} as const;

function KycSlotImage({ recruiterId, slot }: { recruiterId: string; slot: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    adminApi
      .fetchKycFile(recruiterId, slot)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load image");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [recruiterId, slot]);

  if (error) {
    return (
      <Text size="sm" c="red">
        {error}
      </Text>
    );
  }
  if (!url) {
    return <LoadingOverlay height="var(--axio-unit-180)" />;
  }
  return (
    <img
      src={url}
      alt={SLOT_LABELS[slot] ?? slot}
      style={{ width: "var(--axio-percentage-full)", maxHeight: "var(--axio-unit-280)", objectFit: "contain", borderRadius: axioTokens.radius.md, background: axioTokens.mediaBackground }}
    />
  );
}

export function AdminRecruiterDetailPage() {
  const { recruiterId } = useParams<{ recruiterId: string }>();
  const queryClient = useQueryClient();
  const { flatOptions: segmentOptions } = useSegmentCatalog();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [approveSegments, setApproveSegments] = useState<string[]>([]);
  const [profileSegments, setProfileSegments] = useState<string[]>([]);
  const [editingSegments, setEditingSegments] = useState(false);
  const [accountAction, setAccountAction] = useState<"deactivate" | "reactivate" | null>(null);
  const [accountReason, setAccountReason] = useState("");
  const [accountConfirmation, setAccountConfirmation] = useState("");

  const profileQuery = useQuery({
    queryKey: ["admin-recruiter", recruiterId],
    queryFn: () => adminApi.getRecruiter(recruiterId!),
    enabled: !!recruiterId,
  });
  const kycQuery = useQuery({
    queryKey: ["admin-recruiter-kyc", recruiterId],
    queryFn: () => adminApi.getRecruiterKyc(recruiterId!),
    enabled: !!recruiterId,
  });
  const timelineQuery = useQuery({
    queryKey: ["user-profile-timeline", profileQuery.data?.user.id],
    queryFn: () => profilesApi.getUserTimeline(profileQuery.data!.user.id, { page_size: 100 }),
    enabled: !!profileQuery.data?.user.id,
  });

  const profile = profileQuery.data;
  const kyc = kycQuery.data;
  const filledSlots = kyc?.files.map((f) => f.slot) ?? [];


  const kycMutation = useMutation({
    mutationFn: (payload: { kyc_status: "verified" | "rejected"; notes?: string; segments?: Segment[] }) =>
      adminApi.setRecruiterKyc(recruiterId!, payload),
    onSuccess: (_, vars) => {
      notifications.show({
        color: vars.kyc_status === "verified" ? "green" : "red",
        title: vars.kyc_status === "verified" ? "Recruiter approved" : "Recruiter rejected",
        message: vars.kyc_status === "verified" ? "KYC access has been approved." : "KYC was returned for correction.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-recruiter", recruiterId] });
      queryClient.invalidateQueries({ queryKey: ["admin-recruiter-kyc", recruiterId] });
      queryClient.invalidateQueries({ queryKey: ["admin-recruiters"] });
      setApproveOpen(false);
      setRejectOpen(false);
      setNotes("");
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Update failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const segmentsMutation = useMutation({
    mutationFn: (segments: Segment[]) =>
      adminApi.setRecruiterKyc(recruiterId!, {
        kyc_status: profileQuery.data!.kyc_status,
        segments,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: SEGMENT_SELECT_COPY.adminSaved });
      setEditingSegments(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-recruiter", recruiterId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-recruiters"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Update failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const accountMutation = useMutation({
    mutationFn: ({ action, userId, reason }: { action: "deactivate" | "reactivate"; userId: string; reason: string }) =>
      action === "deactivate"
        ? adminApi.deactivateUser(userId, reason)
        : adminApi.reactivateUser(userId, reason),
    onSuccess: (_, variables) => {
      notifications.show({
        color: "green",
        message: `Recruiter account ${variables.action === "deactivate" ? "deactivated" : "reactivated"}.`,
      });
      setAccountAction(null);
      setAccountReason("");
      setAccountConfirmation("");
      void queryClient.invalidateQueries({ queryKey: ["admin-recruiter", recruiterId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-recruiters"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-people"] });
    },
    onError: (err) => notifications.show({
      color: "red",
      message: err instanceof ApiError ? err.message : "Could not change account access",
    }),
  });

  function openApprove() {
    setApproveSegments(profile?.segments ?? []);
    setNotes("");
    setApproveOpen(true);
  }

  function startEditSegments() {
    setProfileSegments(profile?.segments ?? []);
    setEditingSegments(true);
  }

  function cancelEditSegments() {
    setProfileSegments(profile?.segments ?? []);
    setEditingSegments(false);
  }

  if (profileQuery.isLoading || kycQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-320)" />;
  }
  if (!profile) {
    return <Text c="dimmed">Recruiter not found.</Text>;
  }
  const isDeactivated = profile.user.status === "suspended";
  const closeAccountModal = () => {
    setAccountAction(null);
    setAccountReason("");
    setAccountConfirmation("");
  };

  return (
    <Stack gap="lg">
      <Button component={Link} to="/admin/recruiters" variant="subtle" w="fit-content">
        ← {RECRUITER_PROFILE_COPY.back}
      </Button>

      <ProfileShell
        name={profile.user.full_name}
        subtitle={`${profile.user.email}${profile.user.phone ? ` · ${profile.user.phone}` : ""}`}
        role={RECRUITER_PROFILE_COPY.role}
        status={profile.kyc_status}
        actions={
          <>
            {(profile.kyc_status === "pending" || profile.kyc_status === "submitted") && (
              <>
                <Button color="green" onClick={openApprove}>Approve</Button>
                <Button color="red" variant="light" onClick={() => setRejectOpen(true)}>
                  {ACCESS_GRANT_COPY.rejectCta}
                </Button>
              </>
            )}
            <Button
              color={isDeactivated ? "teal" : "red"}
              variant={isDeactivated ? "light" : "filled"}
              onClick={() => setAccountAction(isDeactivated ? "reactivate" : "deactivate")}
            >
              {isDeactivated
                ? RECRUITER_PROFILE_COPY.reactivateCta
                : RECRUITER_PROFILE_COPY.deactivateCta}
            </Button>
          </>
        }
        stats={
          <ProfileKeyFacts
            facts={[
              { label: "City", value: profile.city ?? "—" },
              { label: "Registered", value: formatDate(profile.created_at) },
              { label: "KYC submitted", value: kyc?.kyc_submitted_at ? formatDate(kyc.kyc_submitted_at) : "—" },
              { label: "Tier", value: profile.tier },
            ]}
          />
        }
        beforeTabs={
            <Paper withBorder p="md">
              <Stack gap="md">
                <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                  <Title order={5}>{SEGMENT_SELECT_COPY.adminTitle}</Title>
                  {!editingSegments && (
                    <Button size="xs" variant="default" onClick={startEditSegments}>
                      {SEGMENT_SELECT_COPY.adminEdit}
                    </Button>
                  )}
                </Group>
                {editingSegments ? (
                  <>
                    <MultiSelect
                      label={SEGMENT_SELECT_COPY.recruitForLabel}
                      withAsterisk
                      searchable
                      data={segmentOptions}
                      renderOption={renderSegmentOption}
                      filter={catalogFuzzyFilter}
                      nothingFoundMessage={SEGMENT_SELECT_COPY.nothingFound}
                      value={profileSegments}
                      onChange={setProfileSegments}
                    />
                    <Group gap="sm">
                      <Button
                        w="fit-content"
                        color="violet"
                        disabled={profileSegments.length === 0}
                        loading={segmentsMutation.isPending}
                        onClick={() => segmentsMutation.mutate(profileSegments as Segment[])}
                      >
                        {SEGMENT_SELECT_COPY.adminSave}
                      </Button>
                      <Button
                        variant="default"
                        disabled={segmentsMutation.isPending}
                        onClick={cancelEditSegments}
                      >
                        {CONFIRM_DIALOG_COPY.cancel}
                      </Button>
                    </Group>
                  </>
                ) : profile.segments.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    {SEGMENT_SELECT_COPY.empty}
                  </Text>
                ) : (
                  <Group gap="xs" wrap="wrap">
                    {profile.segments.map((segment) => (
                      <Badge key={segment} color="violet" variant="light">
                        {SEGMENT_LABELS[segment] ?? segment}
                      </Badge>
                    ))}
                  </Group>
                )}
              </Stack>
            </Paper>
        }
        timeline={
          timelineQuery.isLoading ? (
            <LoadingOverlay height="var(--axio-unit-220)" />
          ) : (
            <ProfileTimeline entries={timelineQuery.data?.items ?? []} />
          )
        }
        details={
          <Stack gap="md">
            <Alert color="gray" title={kycPackLabel(profile.kyc_status, profile.kyc_documents_complete)}>
              {profile.kyc_notes ? `Notes: ${profile.kyc_notes}` : "Private KYC profile — images are admin-only."}
            </Alert>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Paper withBorder p="md">
                <Title order={5} mb="sm">Identity &amp; access</Title>
                <Stack gap="var(--axio-unit-6)">
                  <Text size="sm">Email: {profile.user.email}</Text>
                  <Text size="sm">Phone: {profile.user.phone ?? "—"}</Text>
                  <Text size="sm">Auth provider: {profile.user.auth_provider}</Text>
                  {profile.consultancy_name && <Text size="sm">Consultancy: {profile.consultancy_name}</Text>}
                  <Group gap="xs">
                    {(profile.user.auth_provider === "google" || profile.user.email_verified_at) && (
                      <Badge size="xs" color="green" variant="light">Email verified</Badge>
                    )}
                    {profile.user.phone_verified_at && (
                      <Badge size="xs" color="green" variant="light">Phone verified</Badge>
                    )}
                  </Group>
                </Stack>
              </Paper>
              <Paper withBorder p="md">
                <Title order={5} mb="sm">Recruiter details</Title>
                <Stack gap="var(--axio-unit-6)">
                  <Text size="sm">City: {profile.city ?? "—"}</Text>
                  <Text size="sm">Aadhaar: {kyc?.aadhaar_masked ?? "—"}</Text>
                  <Text size="sm">PAN: {kyc?.pan ?? "—"}</Text>
                </Stack>
              </Paper>
            </SimpleGrid>
          </Stack>
        }
        documents={
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Title order={4}>KYC documents</Title>
                <Text size="sm" c="dimmed">Private uploads available only to administrators.</Text>
              </div>
              <ResumeLink url={profile.resume_url ?? kyc?.resume_url ?? null} variant="link" />
            </Group>
            {filledSlots.length === 0 ? (
              <Paper withBorder p="lg">
                <Text c="dimmed" size="sm">No documents uploaded yet.</Text>
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {filledSlots.map((slot) => (
                  <Paper key={slot} withBorder p="md">
                    <Stack gap="sm">
                      <Text fw="var(--axio-font-weight-semibold)" size="sm">{SLOT_LABELS[slot] ?? slot}</Text>
                      <KycSlotImage recruiterId={profile.id} slot={slot} />
                    </Stack>
                  </Paper>
                ))}
              </SimpleGrid>
            )}
          </Stack>
        }
      />

      <Modal opened={approveOpen} onClose={() => setApproveOpen(false)} title="Approve recruiter" centered>
        <Stack>
          <MultiSelect
            label={SEGMENT_SELECT_COPY.recruitForLabel}
            withAsterisk
            searchable
            data={segmentOptions}
            renderOption={renderSegmentOption}
            filter={catalogFuzzyFilter}
            nothingFoundMessage={SEGMENT_SELECT_COPY.nothingFound}
            value={approveSegments}
            onChange={setApproveSegments}
          />
          <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
          <Button
            color="green"
            disabled={approveSegments.length === 0}
            loading={kycMutation.isPending}
            onClick={() =>
              kycMutation.mutate({
                kyc_status: "verified",
                notes: notes || undefined,
                segments: approveSegments as Segment[],
              })
            }
          >
            Approve recruiter
          </Button>
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={ACCESS_GRANT_COPY.rejectRecruiterTitle}
        description={ACCESS_GRANT_COPY.rejectRecruiterBody}
        confirmLabel={ACCESS_GRANT_COPY.rejectKycCta}
        loading={kycMutation.isPending}
        confirmDisabled={!notes.trim()}
        onConfirm={() => kycMutation.mutate({ kyc_status: "rejected", notes: notes.trim() })}
      >
        <Textarea
          label={ACCESS_GRANT_COPY.reasonLabel}
          required
          placeholder={ACCESS_GRANT_COPY.rejectRecruiterReasonHint}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </ConfirmDialog>

      <Modal
        opened={accountAction !== null}
        onClose={closeAccountModal}
        title={accountAction === "deactivate" ? "Deactivate this recruiter?" : "Reactivate this recruiter?"}
        centered
      >
        <Stack>
          <Text size="sm">
            {accountAction === "deactivate"
              ? "This immediately blocks sign-in and releases active recruiter call claims. Historical records and payouts are retained."
              : "Sign-in access is restored. Calling seats and public intake links stay off until you explicitly re-enable them."}
          </Text>
          <Textarea
            label="Reason"
            required
            minRows={3}
            value={accountReason}
            onChange={(event) => setAccountReason(event.currentTarget.value)}
          />
          <TextInput
            label={`Type ${profile.user.email} to confirm`}
            value={accountConfirmation}
            onChange={(event) => setAccountConfirmation(event.currentTarget.value)}
          />
          <Button
            color={accountAction === "deactivate" ? "red" : "teal"}
            loading={accountMutation.isPending}
            disabled={
              accountReason.trim().length < 3 ||
              accountConfirmation.trim().toLowerCase() !== profile.user.email.toLowerCase()
            }
            onClick={() =>
              accountAction && accountMutation.mutate({
                action: accountAction,
                userId: profile.user.id,
                reason: accountReason.trim(),
              })
            }
          >
            {accountAction === "deactivate" ? "Deactivate recruiter" : "Reactivate recruiter"}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
